/* eslint-disable @typescript-eslint/no-explicit-any */
import { ErrorsEnum, FactoryNotFound, UnknownError } from './errors/index.js';
import type { ServiceLocatorAbstractFactoryContext } from './ServiceLocatorAbstractFactoryContext.js';
import { ServiceLocatorEventBus } from './ServiceLocatorEventBus.js';
import {
  ServiceLocatorInstanceHolder,
  ServiceLocatorInstanceHolderKind,
  ServiceLocatorInstanceHolderStatus,
} from './ServiceLocatorInstanceHolder.js';
import { ServiceLocatorManager } from './ServiceLocatorManager.js';
import type {
  ServiceArgs,
  ServiceEventsArgs,
  ServiceEventsNames,
  ServiceFromInstanceName,
  ServiceInstance,
  ServiceInstanceName,
  ServicesConfig,
  ServicesInstancesNames,
  ServicesNames,
} from './types.js';

export class ServiceLocator<Services extends ServicesConfig = {}> {
  private factories: Map<keyof Services, (...args: any) => Promise<any>> =
    new Map();
  private abstractFactories: Map<
    keyof Services,
    (
      ctx: ServiceLocatorAbstractFactoryContext<Services>,
      ...args: any
    ) => Promise<any>
  > = new Map();
  private readonly eventBus: ServiceLocatorEventBus<Services>;
  private readonly manager: ServiceLocatorManager<Services>;

  constructor(private readonly logger: Console | null = null) {
    this.eventBus = new ServiceLocatorEventBus(logger);
    this.manager = new ServiceLocatorManager(logger);
  }

  getEventBus() {
    return this.eventBus;
  }

  public registerFactory<
    Key extends ServicesNames<Services>,
    Args extends ServiceArgs<Services, Key>,
    Instance extends ServiceInstance<Services, Key, Args>,
  >(name: Key, factory: (...args: Args) => Promise<Instance>) {
    this.logger?.log(
      `[ServiceLocator]#registerFactory(): Registering factory for ${name}`,
    );
    this.factories.set(name, factory);
  }

  public registerAbstractFactory<
    Key extends ServicesNames<Services>,
    Args extends ServiceArgs<Services, Key>,
    Instance extends ServiceInstance<Services, Key, Args>,
  >(
    name: Key,
    factory: (
      ctx: ServiceLocatorAbstractFactoryContext<Services>,
      ...args: Args
    ) => Promise<Instance>,
  ) {
    this.logger?.log(
      `[ServiceLocator]#registerAbstractFactory(): Registering abstract factory for ${name}`,
    );
    this.abstractFactories.set(name, factory);
  }

  public registerInstance<
    Key extends ServicesNames<Services>,
    Args extends ServiceArgs<Services, Key>,
    Instance extends ServiceInstance<Services, Key, Args>,
  >(name: ServiceInstanceName<Services, Key>, instance: Instance) {
    this.logger?.log(
      `[ServiceLocator]#registerInstance(): Registering instance for ${name}`,
    );
    const instanceHolder: ServiceLocatorInstanceHolder<Services, Key, Args> = {
      name,
      instance,
      kind: ServiceLocatorInstanceHolderKind.Instance,
      status: ServiceLocatorInstanceHolderStatus.Created,
      creationPromise: null,
      destroyPromise: null,
      effects: [],
      deps: [],
      destroyListeners: [],
      createdAt: Date.now(),
      ttl: Infinity,
    };
    this.manager.set(name, instanceHolder as any);
  }

  /**
   * Get an instance of a service by its name and arguments
   *
   * It returns a promise with a tuple of error and instance
   * If the instance is not found, and it cannot be created, it will return a FactoryNotFound error
   * If instance creation fails, it will return an UnknownError
   * Otherwise it will return a tuple with undefined as a first item and the instance as a second item
   */
  public async getInstance<
    Key extends ServicesNames<Services>,
    Args extends ServiceArgs<Services, Key>,
    Instance extends ServiceInstance<Services, Key, Args>,
  >(
    name: Key,
    ...args: Args
  ): Promise<[undefined, Instance] | [UnknownError | FactoryNotFound]> {
    const instanceName = this.makeInstanceName(name, args);
    const [error, holder] = this.manager.get(instanceName);
    if (!error) {
      if (holder.status === ServiceLocatorInstanceHolderStatus.Creating) {
        // @ts-expect-error TS2322 We should redefine the instance name type
        return holder.creationPromise;
      } else if (
        holder.status === ServiceLocatorInstanceHolderStatus.Destroying
      ) {
        // Should never happen
        return [new UnknownError(ErrorsEnum.InstanceDestroying)];
      }
      // @ts-expect-error We should redefine the instance name type
      return [undefined, holder.instance];
    }
    switch (error.code) {
      case ErrorsEnum.InstanceDestroying:
        this.logger?.log(
          `[ServiceLocator]#getInstance() TTL expired for ${holder?.name}`,
        );
        await holder?.destroyPromise;
        //Maybe we already have a new instance
        return this.getInstance<Key, Args, Instance>(name, ...args);

      case ErrorsEnum.InstanceExpired:
        this.logger?.log(
          `[ServiceLocator]#getInstance() TTL expired for ${holder?.name}`,
        );
        await this.invalidate(instanceName);
        //Maybe we already have a new instance
        return this.getInstance<Key, Args, Instance>(name, ...args);
      case ErrorsEnum.InstanceNotFound:
        break;
      default:
        return [error];
    }
    return this.createInstance(instanceName, name, args);
  }

  /**
   * Compat method to get an instance of a service by its name and arguments
   * It will throw an error if the instance is not found or cannot be created
   */
  public async getOrThrowInstance<
    Key extends ServicesNames<Services>,
    Args extends ServiceArgs<Services, Key>,
    Instance extends ServiceInstance<Services, Key, Args>,
  >(name: Key, ...args: Args): Promise<Instance> {
    const [error, instance] = await this.getInstance(name, ...args);
    if (error) {
      throw error;
    }
    // @ts-expect-error This is correct type
    return instance;
  }

  private notifyListeners(
    name: ServicesInstancesNames<Services>,
    event: 'create' | 'destroy' = 'create',
  ) {
    this.logger?.log(
      `[ServiceLocator]#notifyListeners() Notifying listeners for ${name} with event ${event}`,
    );
    // @ts-expect-error This is correct type
    return this.eventBus.emit(name, event);
  }

  private async createInstance<
    Key extends ServicesNames<Services>,
    Args extends ServiceArgs<Services, Key>,
    Instance extends ServiceInstance<Services, Key, Args>,
  >(
    instanceName: ServiceInstanceName<Services, Key>,
    name: Key,
    args: Args,
  ): Promise<[undefined, Instance] | [FactoryNotFound | UnknownError]> {
    this.logger?.log(
      `[ServiceLocator]#createInstance() Creating instance for ${instanceName} from ${name}`,
    );
    if (this.factories.has(name)) {
      return this.createInstanceFromFactory(instanceName, name, args);
    } else if (this.abstractFactories.has(name)) {
      return this.createInstanceFromAbstractFactory(instanceName, name, args);
    } else {
      return [new FactoryNotFound(name)];
    }
  }

  private async createInstanceFromFactory<
    Key extends ServicesNames<Services>,
    Args extends ServiceArgs<Services, Key>,
    Instance extends ServiceInstance<Services, Key, Args>,
  >(
    instanceName: ServiceInstanceName<Services, Key>,
    name: Key,
    args: Args,
  ): Promise<[FactoryNotFound | UnknownError] | [undefined, Instance]> {
    this.logger?.log(
      `[ServiceLocator]#createInstanceFromFactory(): Creating instance for ${instanceName} from factory`,
    );
    const factory = this.factories.get(name);
    if (!factory) {
      return [new FactoryNotFound(name)];
    }
    const holder: ServiceLocatorInstanceHolder<Services, Key, Args> = {
      name: instanceName,
      instance: null,
      // @ts-expect-error TS2322 This is correct type
      creationPromise: factory(...args).then(
        async (instance: Instance) => {
          holder.instance = instance;
          holder.status = ServiceLocatorInstanceHolderStatus.Created;
          await this.notifyListeners(name);
          await this.notifyListeners(instanceName);
          return [undefined, instance as Instance];
        },
        (error) => {
          this.logger?.error(
            `[ServiceLocator]#createInstanceFromFactory(): Error creating instance for ${instanceName}`,
            error,
          );
          return [new UnknownError(error)];
        },
      ),
      kind: ServiceLocatorInstanceHolderKind.Factory,
      status: ServiceLocatorInstanceHolderStatus.Creating,
      effects: [],
      deps: [],
      destroyListeners: [],
      createdAt: Date.now(),
      ttl: Infinity,
    };
    // @ts-expect-error We should redefine the instance name type
    this.manager.set(instanceName, holder);

    // @ts-expect-error TS2322 This is correct type
    return holder.creationPromise;
  }

  private async createInstanceFromAbstractFactory<
    Key extends ServicesNames<Services>,
    Args extends ServiceArgs<Services, Key>,
    Instance extends ServiceInstance<Services, Key, Args>,
  >(
    instanceName: ServiceInstanceName<Services, Key>,
    name: Key,
    args: Args,
  ): Promise<[undefined, Instance] | [FactoryNotFound]> {
    this.logger?.log(
      `[ServiceLocator]#createInstanceFromAbstractFactory(): Creating instance for ${instanceName} from abstract factory`,
    );
    const ctx = this.createContextForAbstractFactory(name, instanceName);
    const abstractFactory = this.abstractFactories.get(name);
    if (!abstractFactory) {
      return [new FactoryNotFound(name)];
    }
    const holder: ServiceLocatorInstanceHolder<Services, Key, Args> = {
      name: instanceName,
      instance: null,
      status: ServiceLocatorInstanceHolderStatus.Creating,
      kind: ServiceLocatorInstanceHolderKind.AbstractFactory,
      // @ts-expect-error TS2322 This is correct type
      creationPromise: abstractFactory(ctx, ...args)
        .then(async (instance: Instance) => {
          holder.instance = instance;
          holder.status = ServiceLocatorInstanceHolderStatus.Created;
          holder.deps = ctx.getDependencies();
          holder.destroyListeners = ctx.getDestroyListeners();
          holder.ttl = ctx.getTtl();
          if (holder.deps.length > 0) {
            this.logger?.log(
              `[ServiceLocator]#createInstanceFromAbstractFactory(): Adding subscriptions for ${instanceName} dependencies for their invalidations: ${holder.deps.join(
                ', ',
              )}`,
            );
            holder.deps.forEach((dependency) => {
              holder.destroyListeners.push(
                // @ts-expect-error This is correct type
                this.eventBus.on(dependency, 'destroy', () =>
                  this.invalidate(instanceName),
                ),
              );
            });
          }

          await this.notifyListeners(name);
          await this.notifyListeners(instanceName);
          return [undefined, instance as Instance];
        })
        .catch((error) => {
          this.logger?.error(
            `[ServiceLocator]#createInstanceFromAbstractFactory(): Error creating instance for ${instanceName}`,
            error,
          );
          return [new UnknownError(error)];
        }),
      effects: [],
      deps: [],
      destroyListeners: [],
      createdAt: Date.now(),
      ttl: Infinity,
    };

    // @ts-expect-error We should redefine the instance name type
    this.manager.set(instanceName, holder);
    // @ts-expect-error TS2322 This is correct type
    return holder.creationPromise;
  }

  private createContextForAbstractFactory<Key extends ServicesNames<Services>>(
    holder: Key,
    instanceName: ServicesInstancesNames<Services>,
  ): ServiceLocatorAbstractFactoryContext<Services> {
    const dependencies = new Set<ServicesInstancesNames<Services>>();
    const destroyListeners = new Set<() => void>();
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    async function getInstance<
      Instance extends ServiceInstance<Services, Key, Args>,
      Args extends ServiceArgs<Services, Key>,
      Key extends Exclude<keyof Services, symbol | number>,
    >(
      name: Key,
      ...args: Args
    ): Promise<[undefined, Instance] | [FactoryNotFound | UnknownError]> {
      dependencies.add(self.makeInstanceName(name, args));
      self.logger?.log(
        `[ServiceLocator]#createContextForAbstractFactory('${holder}'): Getting instance for ${name}`,
      );
      return self.getInstance<Key, Args, Instance>(name, ...args);
    }
    function addDependency(name: ServicesInstancesNames<Services>) {
      dependencies.add(name);
    }
    function invalidate(name = instanceName) {
      return self.invalidate(name);
    }
    function addEffect(listener: () => void) {
      destroyListeners.add(listener);
    }
    let ttl = Infinity;
    function setTtl(value: number) {
      ttl = value;
    }
    function getTtl() {
      return ttl;
    }

    function on<
      K extends ServicesInstancesNames<Services>,
      Service extends ServicesNames<Services> = ServiceFromInstanceName<
        Services,
        K
      >,
      E extends ServiceEventsNames<
        Services,
        Service,
        ServiceArgs<Services, Service>
      > = ServiceEventsNames<Services, Service, ServiceArgs<Services, Service>>,
      Args extends ServiceEventsArgs<
        Services,
        Service,
        ServiceArgs<Services, Service>,
        E
      > = ServiceEventsArgs<
        Services,
        Service,
        ServiceArgs<Services, Service>,
        E
      >,
    >(key: K, event: E, listener: (event: E, ...args: Args) => void) {
      // @ts-expect-error This is correct type
      destroyListeners.add(self.eventBus.on(key, event, listener));
    }

    return {
      getInstance,
      // @ts-expect-error This is correct type
      async getOrThrow(name, ...args) {
        const [err, res] = await getInstance(name, ...args);
        if (err) {
          throw err;
        }
        return res;
      },
      invalidate,
      eventBus: self.eventBus,
      on: on as ServiceLocatorEventBus<Services>['on'],
      addDependency,
      getDependencies: () => Array.from(dependencies),
      addEffect,
      getDestroyListeners: () => Array.from(destroyListeners),
      setTtl,
      getTtl,
    };
  }

  public getSyncInstance<
    Key extends ServicesNames<Services>,
    Args extends ServiceArgs<Services, Key>,
    Instance extends ServiceInstance<Services, Key, Args>,
  >(name: Key, ...args: Args): Instance | null {
    const instanceName = this.makeInstanceName(name, args);
    const [error, holder] = this.manager.get(instanceName);
    if (error) {
      return null;
    }
    return holder.instance as Instance;
  }

  invalidate(
    service: ServicesInstancesNames<Services>,
    round = 1,
  ): Promise<any> {
    this.logger?.log(
      `[ServiceLocator]#invalidate(): Starting Invalidating process of ${service}`,
    );
    const toInvalidate = this.manager.filter(
      (holder) =>
        holder.name === service || holder.name.startsWith(`${service}:`),
    );
    const promises = [];
    for (const [key, holder] of toInvalidate.entries()) {
      if (holder.status === ServiceLocatorInstanceHolderStatus.Destroying) {
        this.logger?.trace(
          `[ServiceLocator]#invalidate(): ${key} is already being destroyed`,
        );
        promises.push(holder.destroyPromise);
        continue;
      }
      if (holder.status === ServiceLocatorInstanceHolderStatus.Creating) {
        this.logger?.trace(
          `[ServiceLocator]#invalidate(): ${key} is being created, waiting for creation to finish`,
        );
        promises.push(
          holder.creationPromise.then(() => {
            if (round > 3) {
              this.logger?.error(
                `[ServiceLocator]#invalidate(): ${key} creation is triggering a new invalidation round, but it is still not created`,
              );
              return;
            }
            return this.invalidate(key, round + 1);
          }),
        );
        continue;
      }
      // @ts-expect-error TS2322 we are changing the status
      holder.status = ServiceLocatorInstanceHolderStatus.Destroying;
      this.logger?.log(
        `[ServiceLocator]#invalidate(): Invalidating ${key} and notifying listeners`,
      );
      // @ts-expect-error TS2322 we are changing the status
      holder.destroyPromise = Promise.all(
        holder.destroyListeners.map((listener) => listener()),
      ).then(async () => {
        this.manager.delete(key);
        await this.notifyListeners(key, 'destroy');
      });
      promises.push(holder.destroyPromise);
    }
    return Promise.all(promises);
  }

  async ready() {
    return Promise.all(
      Array.from(this.manager.filter(() => true)).map(([, holder]) => {
        if (holder.status === ServiceLocatorInstanceHolderStatus.Creating) {
          return holder.creationPromise.then(() => null);
        }
        if (holder.status === ServiceLocatorInstanceHolderStatus.Destroying) {
          return holder.destroyPromise.then(() => null);
        }
        return Promise.resolve(null);
      }),
    ).then(() => null);
  }

  makeInstanceName(
    name: ServicesNames<Services>,
    args: any,
  ): ServicesInstancesNames<Services> {
    if (args.length === 0) {
      return name as unknown as ServicesInstancesNames<Services>;
    }
    return `${name as Exclude<keyof Services, symbol | number>}:${JSON.stringify(
      args,
    )
      .replace('[', '')
      .replace(']', '')
      .replace(/"/g, '')}` as ServicesInstancesNames<Services>;
  }
}
